import type { NavigateFunction } from "react-router-dom";
import {
  createFavourite,
  createFavouritesBulk,
  FAVOURITES_PAGE_SIZE,
  getUserFavourites,
  isFavouriteArtSavedLocally,
  isFavouriteImageInList,
  normalizeFavouriteImageKey,
  persistFavouriteArtSavedLocally,
  type Favourite,
} from "../services/favouritesService";
import type { AuthUser } from "../modal/auth/types";
import { resolveAuthAccessToken } from "./authSession";
import { resolveSelectedAiSelfieArtFavouriteType } from "../services/stylePromptResolver";

export type HobbyGenerationResult = {
  imageUrls: string[];
  /** Parallel to `imageUrls`: same characters without Mosida logo (for transparent composite). */
  imageUrlsNoLogo?: string[];
  raw?: unknown;
};

export const HOBBY_GENERATION_STORAGE_KEY = "hobbyCaricatureGenerationResult";
/** Individual character caricature URLs pending bulk favourite save after login. */
export const HOBBY_CHARACTER_FAVOURITE_URLS_KEY = "hobbyCharacterFavouriteUrls";
/** Maps logo character URL → no-logo URL from batch generation. */
export const HOBBY_CHARACTER_NOLOGO_MAP_KEY = "hobbyCharacterNologoByUrl";

export function persistHobbyGenerationResult(result: HobbyGenerationResult): void {
  try {
    localStorage.setItem(HOBBY_GENERATION_STORAGE_KEY, JSON.stringify(result));
  } catch {
    /* ignore storage errors */
  }
  if (result.imageUrls?.length) {
    persistHobbyCharacterNologoPairs(result.imageUrls, result.imageUrlsNoLogo);
  }
}

/** Merge logo → no-logo URL pairs (kept across multi-person hobby generations). */
export function persistHobbyCharacterNologoPairs(
  imageUrls: string[],
  imageUrlsNoLogo?: string[] | null
): void {
  const logos = normalizeCharacterUrlList(imageUrls);
  if (!logos.length) return;
  const noLogos = normalizeCharacterUrlList(imageUrlsNoLogo);
  try {
    const existingRaw = localStorage.getItem(HOBBY_CHARACTER_NOLOGO_MAP_KEY);
    const map: Record<string, string> =
      existingRaw && typeof existingRaw === "string"
        ? (JSON.parse(existingRaw) as Record<string, string>) || {}
        : {};
    logos.forEach((logoUrl, index) => {
      const noLogo = noLogos[index];
      if (noLogo) {
        map[logoUrl] = noLogo;
      }
    });
    localStorage.setItem(HOBBY_CHARACTER_NOLOGO_MAP_KEY, JSON.stringify(map));
  } catch {
    /* ignore storage errors */
  }
}

export function clearHobbyCharacterNologoMap(): void {
  try {
    localStorage.removeItem(HOBBY_CHARACTER_NOLOGO_MAP_KEY);
  } catch {
    /* ignore */
  }
}

/** For transparent backgrounds, prefer the no-logo character URL from batch generation. */
export function resolveHobbyCharacterUrlForComposite(
  url: string,
  useNoLogo: boolean
): string {
  const trimmed = (url || "").trim();
  if (!trimmed || !useNoLogo) return trimmed;
  try {
    const raw = localStorage.getItem(HOBBY_CHARACTER_NOLOGO_MAP_KEY);
    if (!raw) return trimmed;
    const map = JSON.parse(raw) as Record<string, string>;
    const noLogo = typeof map?.[trimmed] === "string" ? map[trimmed].trim() : "";
    return noLogo || trimmed;
  } catch {
    return trimmed;
  }
}

export function resolveHobbyCharacterUrlsForComposite(
  urls: string[],
  useNoLogo: boolean
): string[] {
  return (urls || [])
    .map((url) => resolveHobbyCharacterUrlForComposite(url, useNoLogo))
    .filter((url) => url.length > 0);
}

function normalizeCharacterUrlList(urls: unknown): string[] {
  if (!Array.isArray(urls)) return [];
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const url of urls) {
    if (typeof url !== "string") continue;
    const trimmed = url.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    normalized.push(trimmed);
  }
  return normalized;
}

export function readHobbyCharacterFavouriteUrls(): string[] {
  try {
    const raw = localStorage.getItem(HOBBY_CHARACTER_FAVOURITE_URLS_KEY);
    if (!raw) return [];
    return normalizeCharacterUrlList(JSON.parse(raw));
  } catch {
    return [];
  }
}

export function persistHobbyCharacterFavouriteUrls(urls: unknown): void {
  const normalized = normalizeCharacterUrlList(urls);
  try {
    localStorage.setItem(HOBBY_CHARACTER_FAVOURITE_URLS_KEY, JSON.stringify(normalized));
  } catch {
    /* ignore storage errors */
  }
}

export function appendHobbyCharacterFavouriteUrl(url: unknown): void {
  if (typeof url !== "string" || !url.trim()) return;
  persistHobbyCharacterFavouriteUrls([
    ...readHobbyCharacterFavouriteUrls(),
    url.trim(),
  ]);
}

export function clearHobbyCharacterFavouriteUrls(): void {
  try {
    localStorage.removeItem(HOBBY_CHARACTER_FAVOURITE_URLS_KEY);
  } catch {
    /* ignore storage errors */
  }
}

/** Resolve pending character URLs from storage, route state, or generation result. */
export function resolveHobbyCharacterFavouriteUrls(
  routeUrls?: unknown
): string[] {
  const fromRoute = normalizeCharacterUrlList(routeUrls);
  if (fromRoute.length > 0) return fromRoute;

  const fromPending = readHobbyCharacterFavouriteUrls();
  if (fromPending.length > 0) return fromPending;

  try {
    const raw = localStorage.getItem(HOBBY_GENERATION_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as HobbyGenerationResult;
    return normalizeCharacterUrlList(parsed?.imageUrls);
  } catch {
    return [];
  }
}

/** After login, save pending character URLs via bulk API and clear storage on success. */
export async function syncPendingHobbyCharacterFavouritesAfterLogin(options: {
  accessToken: string;
  routeUrls?: unknown;
  fallbackUrls?: string[];
  ticketId?: string | null;
  type?: string;
  skipImageUrls?: string[];
}): Promise<{ success: boolean; skipped?: boolean; error?: string }> {
  const resolved = resolveHobbyCharacterFavouriteUrls(options.routeUrls);
  const fallback = normalizeCharacterUrlList(options.fallbackUrls);
  const skipKeys = new Set(
    (options.skipImageUrls || []).map(normalizeFavouriteImageKey).filter(Boolean)
  );
  const imageUrls = (resolved.length > 0 ? resolved : fallback).filter(
    (url) => !skipKeys.has(normalizeFavouriteImageKey(url))
  );

  if (imageUrls.length === 0) {
    return { success: true, skipped: true };
  }

  const favouriteType = options.type?.trim() || resolveSelectedAiSelfieArtFavouriteType();

  const result = await createFavouritesBulk({
    accessToken: options.accessToken,
    imageUrls,
    type: favouriteType,
  });

  if (result.success && !result.skipped) {
    clearHobbyCharacterFavouriteUrls();
  }

  if (!result.success) {
    return { success: false, error: result.error || "Failed to save character favourites." };
  }

  return { success: true, skipped: result.skipped };
}

/** Save composite art + pending hobby character URLs after login. */
export async function saveArtAndHobbyFavouritesAfterLogin(options: {
  user: AuthUser;
  artUrl?: string | null;
  ticketId?: string | null;
  routeCharacterUrls?: unknown;
  fallbackCharacterUrls?: string[];
  /** Favourites API `type` (e.g. "Pet Caricature", "Caricature Hobby"). */
  type?: string;
  /** Skip getUserFavourites when caller already loaded page 1. */
  existingFavourites?: Favourite[] | null;
}): Promise<{
  success: boolean;
  error?: string;
  createdArt?: boolean;
  createdBulk?: boolean;
  favourites?: Favourite[];
}> {
  const accessToken = resolveAuthAccessToken(options.user);
  if (!accessToken) {
    return { success: false, error: "Authentication token is missing." };
  }

  const favouriteType =
    options.type?.trim() || resolveSelectedAiSelfieArtFavouriteType();

  const artUrl = options.artUrl?.trim();
  if (artUrl) {
    try {
      localStorage.setItem("favouriteImg", artUrl);
    } catch {
      /* ignore storage errors */
    }
  }

  let existingFavourites: Favourite[];
  if (Array.isArray(options.existingFavourites)) {
    existingFavourites = options.existingFavourites;
  } else {
    const existingResponse = await getUserFavourites(options.user.id, {
      page: 1,
      limit: FAVOURITES_PAGE_SIZE,
      accessToken,
    });
    existingFavourites = existingResponse.success ? existingResponse.data || [] : [];
  }
  const alreadySavedLocally = Boolean(artUrl && isFavouriteArtSavedLocally(artUrl));
  const alreadyInFavourites = Boolean(
    artUrl && isFavouriteImageInList(artUrl, existingFavourites)
  );
  const needsCreateFavourite =
    Boolean(artUrl) && !alreadySavedLocally && !alreadyInFavourites;

  const favouriteResult = needsCreateFavourite
    ? await createFavourite({
        userId: options.user.id,
        imageUrl: artUrl,
        favouriteType,
        type: favouriteType,
        ticketId: options.ticketId ?? undefined,
      })
    : { success: true as const };

  if (!favouriteResult.success) {
    return {
      success: false,
      error: favouriteResult.error || "Failed to save your favourite.",
    };
  }

  if (artUrl && (needsCreateFavourite || alreadyInFavourites || alreadySavedLocally)) {
    persistFavouriteArtSavedLocally(artUrl);
  }

  const bulkResult = await syncPendingHobbyCharacterFavouritesAfterLogin({
    accessToken,
    routeUrls: options.routeCharacterUrls,
    fallbackUrls: options.fallbackCharacterUrls,
    type: favouriteType,
    ticketId: options.ticketId,
    skipImageUrls: existingFavourites.map((item) => item.imageUrl).concat(artUrl ? [artUrl] : []),
  });

  if (!bulkResult.success && !bulkResult.skipped) {
    return {
      success: false,
      error: bulkResult.error || "Failed to save character favourites.",
    };
  }

  return {
    success: true,
    createdArt: needsCreateFavourite,
    createdBulk: Boolean(bulkResult.success && !bulkResult.skipped),
    favourites: existingFavourites,
  };
}

export function getPrimaryPreviewUrl(result?: HobbyGenerationResult | null): string | null {
  const url = result?.imageUrls?.find((item) => typeof item === "string" && item.trim());
  return url?.trim() ? url : null;
}

export function navigateToCreatedPersonHobby(
  navigate: NavigateFunction,
  routeSlug: string,
  generationResult: HobbyGenerationResult,
  stationData?: Record<string, unknown>,
  options?: {
    currentPersonIndex?: number;
    isFinalCombine?: boolean;
  }
): void {
  persistHobbyGenerationResult(generationResult);
  navigate(`/${routeSlug}/created-person-hobby`, {
    replace: true,
    state: {
      generationResult,
      stationData,
      currentPersonIndex: options?.currentPersonIndex,
      isFinalCombine: options?.isFinalCombine,
    },
  });
}
